<template>
	<div v-if="isVisible" class="px-4 formField">
		<v-text-field
			:disabled="formField.Readonly"
			@keyup="uppercase"
			:class="getUppercaseClass"
			:rules="validationRules"
			:error-messages="validationError"
			v-model="selectedValue"
			@focus="onFocus"
			@blur="onBlur"
			:label="formField.Label"
			clearable
			hide-details="auto"
		>
			<template v-slot:prepend-inner>
				<v-icon color="primary">mdi-key-variant</v-icon>
			</template>
			<template v-slot:prepend v-if="visibleBubble || visibleHelp">
				<div v-if="visibleBubble">
					<v-tooltip
						top
						nudge-top="20"
						:open-on-click="true"
						:open-on-focus="true"
						:open-on-hover="true"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-icon
								color="primary"
								v-bind="attrs"
								v-on="on"
								tabindex="-1"
								>mdi-information-outline</v-icon
							>
						</template>
						{{ bubbleText }}
					</v-tooltip>
				</div>
				<div v-if="visibleHelp">
					<v-tooltip
						top
						nudge-top="20"
						:open-on-click="true"
						:open-on-focus="true"
						:open-on-hover="true"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-icon
								color="primary"
								v-bind="attrs"
								v-on="on"
								tabindex="-1"
								>mdi-help-circle-outline</v-icon
							>
						</template>
						{{ helpText }}
					</v-tooltip>
				</div>
			</template>
			<template v-slot:append-outer v-if="canRetainValue">
				<v-icon v-if="!hasRetainedValue" @click="onPinClick"
					>mdi-pin-off-outline</v-icon
				>
				<v-icon
					v-else
					color="primary"
					class="rotate45"
					@click="onPinClick"
					>mdi-pin-outline</v-icon
				>
			</template>
		</v-text-field>
		<LinkFieldModal
			v-if="currentGridView && showDialog && inventoryForm"
			@close="closeDialog()"
			@rowClicked="onRowClicked"
			:dialog="showDialog"
			:gridView="currentGridView"
			:filter="filter"
			:modalTitle="modalTitle"
			:action="loadAction"
			:inventoryFormId="inventoryForm.Id"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { gridViewTypes } from '../../../../enums/gridViewTypes';
import formFieldMixin from '../../../../mixins/inventoryForm/formFieldMixin';
import {
	InventoryFormFormGetters,
	InventoryFormGetters,
} from '../../../../store/getters';
import {
	InventoryFormFormActions,
	GeneralActions,
} from '../../../../store/actions';
import LinkFieldModal from '../LinkFieldModal.vue';
import formFieldOnScanEditUtils from '../../../../utils/formFieldOnScanEditUtils';
import retainedValuesUtils from '../../../../utils/retainedValuesUtils';

export default {
	name: 'PrimaryKeyField',
	components: { LinkFieldModal },
	props: {
		formField: Object,
	},
	mixins: [formFieldMixin],
	data() {
		return {
			showDialog: false,
			gridData: [],
			filter: {},
			modalTitle: 'Chose a record',
			loadAction: InventoryFormFormActions.LOAD_GRID_VIEW_DATA,
		};
	},
	mounted() {
		this.defaultRules.push((v) => {
			if (this.formField.Required && !this.isBulkEdit) {
				if (!v) return false || 'The value is required';
				else return true;
			} else return true;
		});
		if (this.recordSelectedValue) {
			this.selectedValue = this.recordSelectedValue;
		}
	},

	computed: {
		...mapGetters({
			inventoryForm: [InventoryFormFormGetters.SELECTED_INVENTORY_FORM],
			gridViews: [InventoryFormFormGetters.GRID_VIEWS],
			defaultGridViews: [InventoryFormGetters.GET_GRID_VIEWS],
		}),
		gridViewField() {
			if (!this.formField) return null;
			return this.formField.GridViewFields[0];
		},
		currentGridViewEntityId() {
			if (!this.gridViewField.DataField) return null;
			return this.gridViewField.DataField.EntityId;
		},
		currentGridView() {
			if (!this.currentGridViewEntityId) return null;
			let entityId = this.currentGridViewEntityId;
			let gv = this.gridViews.find(
				(x) =>
					x.Type === gridViewTypes.InventoryFormFilters &&
					x.EntityId == entityId
			);
			if (gv) {
				return gv;
			} else {
				gv = this.defaultGridViews.find(
					(x) =>
						x.Type === gridViewTypes.Grids &&
						x.EntityId == entityId &&
						x.UserId == null
				);
				return gv;
			}
		},
		currentGridViewColumnsId() {
			let formFields = [];
			this.inventoryForm.FormFields.forEach((i) => {
				i.GridViewFieldIds.forEach((id) => {
					formFields.push(id);
				});
			});
			return formFields;
		},
	},
	methods: {
		onSelectedValueChanged(newVal) {
			this.skipNextRecordUpdate = true;
			this.$emit('selectedValueChanged', [
				{ key: this.recordKeys[0], value: newVal },
			]);
		},
		onRecordChanged() {
			if (
				this.recordSelectedValue &&
				this.selectedValue != this.recordSelectedValue
			) {
				this.selectedValue = this.recordSelectedValue;
				return;
			}

			if (
				this.selectedValue &&
				this.selectedValue != this.recordSelectedValue
			) {
				this.selectedValue = null;
			}
		},
		onHideFormField() {
			this.selectedValue = null;
		},

		onFocus() {
			this.$emit('pkFormFieldFocusChanged', true);
		},

		onBlur() {
			if (this.selectedValue && !this.record.Id && this.loadPk) {
				this.checkForData()
					.then((resp) => {
						if (resp.Count > 1) {
							this.filter = {
								FieldId: this.formField.GridViewFields[0].Id,
								Operator: 1,
								Value: this.selectedValue,
							};
							this.openDialog();
						}
						if (resp.Count == 1) {
							this.onRowClicked(resp.Data[0]);
						}
					})
					.finally(() => {
						this.$emit('pkFormFieldFocusChanged', false);
					});
			} else {
				this.$emit('pkFormFieldFocusChanged', false);
			}
		},
		openDialog() {
			this.showDialog = true;
		},
		closeDialog() {
			this.showDialog = false;
		},

		onRowClicked(selectedRecord) {
			// the keys holding a pinned value - the scanned record is not allowed
			// to write over them, neither the displayed value nor the link id
			let retainedKeys = retainedValuesUtils.getRetainedValueKeys(
				(this.retainedValues || []).filter(
					(x) =>
						x.userId === this.currentUserId &&
						x.inventoryFormId === this.inventoryForm.Id
				)
			);
			let payload =
				formFieldOnScanEditUtils.getAppliedAndRemovedKeysValuePair(
					this.inventoryForm,
					selectedRecord,
					this.record,
					retainedKeys
				).applied;
			this.$emit('selectedValueChanged', payload);
			this.closeDialog();
		},
		checkForData() {
			return new Promise((resolve) => {
				this.$store.dispatch(GeneralActions.LOADING, true);
				let payload = {
					InventoryFormId: this.inventoryForm.Id,
					ViewId: this.currentGridView.Id,
					AdvancedFilter: {
						Operator: 1,
						Filters: [
							{
								FieldId: this.formField.GridViewFields[0].Id,
								Operator: 1,
								Value: this.selectedValue,
							},
						],
					},
					Page: 1,
					RowsPerPage: 10,
					Columns: this.currentGridViewColumnsId,
					IncludeCount: true,
				};
				this.$store
					.dispatch(
						InventoryFormFormActions.LOAD_GRID_VIEW_DATA,
						payload
					)
					.then((resp) => {
						this.$store.dispatch(GeneralActions.LOADING, false);
						resolve(resp);
					});
			});
		},
		onSetInteraction(setValue) {
			this.onSelectedValueChanged(setValue);
		},
		onClearInteraction() {
			this.onSelectedValueChanged(null);
		},
	},
};
</script>
